import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, filter, map, of, tap, withLatestFrom } from 'rxjs';
import { HealthApiService } from '../health-api.service';
import { type HealthFilterCountForAPI, HealthFilterName, UserConfigHealthMonitorFilters } from '../health-api.types';
import { HealthMonitorFilterType, type HistoryUpdate } from '../health-monitor.types';
import { HealthMonitorActions } from './health-monitor.actions';
import {
  selectAlarmsDateRange,
  selectAlarmsStatisticsWidgetFilterPayload,
  selectBatteryCapacityWidgetFilterPayload,
  selectDeviceTypesFilters,
  selectHealthMonitorFiltersPayload,
  selectHealthReadRateWidgetFilterPayload,
  selectHistoryLogDateRange,
  selectOtherWidgetFilterPayload,
  selectReadSuccessRateWidgetFilterPayload,
  selectSelectedDevicesFilters
} from './health-monitor.reducer';
import { calculateTotalCountBatteryCapacity } from './mappers/battery-status.mappers';
import { createHealthStatistic } from './mappers/overall-health.mappers';
import { calculateReadRateStatistic } from './mappers/read-rate.mappers';
import { ReportGroupFilterApiService } from '../../../../services/api/report-group-filter/report-group-filter-api.service';
import { DateUtil } from 'src/app/shared/utils/date-util';
import { UserConfigService } from 'src/app/services/utility/user-config.service';
import { HealthMonitorDateRangeService } from '../health-monitor.date-range.service';
import { resolveDateRangeFromPreset } from '../../../../shared/components/datepicker/datepicker-preset.utils';
import { DatepickerRange } from '../../../../shared/components/datepicker/datepicker.types';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import { initialReadRateStatistic } from './health-monitor.state';

const USER_CONFIG_FILTERS_STORAGE_KEY = 'health-monitor_filters';
const APPLICABLE = 'AMI Health Dashboard';

const healthFilterCountPropertiesMap: ReadonlyMap<HealthMonitorFilterType, keyof HealthFilterCountForAPI> = new Map([
  ['dcu', 'dcuCount'],
  ['mtu', 'mtuCount'],
  ['sensors', 'sensorsCount']
]);

function getHealthFilterCountProperty(filterType: HealthMonitorFilterType): keyof HealthFilterCountForAPI {
  const property = healthFilterCountPropertiesMap.get(filterType);
  if (!property) {
    throw new Error(
      `[AOS. getHealthFilterCountProperty] The \`HealthMonitorFilterType\` ('${filterType}') does not exist.`
    );
  }
  return property;
}

export const getFiltersState$ = createEffect(
  (
    actions$ = inject(Actions),
    userConfigService = inject(UserConfigService),
    dateRangeService = inject(HealthMonitorDateRangeService)
  ) =>
    actions$.pipe(
      ofType(HealthMonitorActions.getPersistedFiltersState),
      exhaustMap(() =>
        userConfigService.getGridSettings<UserConfigHealthMonitorFilters>('health-monitor').pipe(
          map(s => s?.filters ?? null),
          map(state => {
            const resolveRange = (range: DatepickerRange | undefined): DatepickerRange => {
              if (!range) {
                return dateRangeService.createDefaultDateRange();
              }

              const resolved = range.presetId ? resolveDateRangeFromPreset(range.presetId) : null;
              if (resolved) {
                return resolved;
              }

              return {
                start: new Date(range.start),
                end: new Date(range.end)
              };
            };
            const alarmsDateRange = resolveRange(state?.alarmsDateRange);
            const historyLogDateRange = resolveRange(state?.historyLogDateRange);
            return HealthMonitorActions.getFiltersAndInitFiltersState({
              filtersState: { ...state!, alarmsDateRange, historyLogDateRange }
            });
          }),
          catchError(() => of(HealthMonitorActions.getFiltersAndInitFiltersState({ filtersState: null })))
        )
      )
    ),
  { functional: true }
);

export const getFiltersAndInitFiltersState$ = createEffect(
  (actions$ = inject(Actions), reportGroupFilterApiService = inject(ReportGroupFilterApiService)) =>
    actions$.pipe(
      ofType(HealthMonitorActions.getFiltersAndInitFiltersState),
      exhaustMap(({ filtersState }) =>
        reportGroupFilterApiService.getReportGroup(APPLICABLE).pipe(
          map(reportGroups =>
            HealthMonitorActions.getFiltersAndInitFiltersStateSuccess({ reportGroups, filtersState })
          ),
          catchError((error: { message: string }) =>
            of(HealthMonitorActions.getFiltersAndInitFiltersStateFailure({ error: error.message }))
          )
        )
      )
    ),
  { functional: true }
);

export const loadDeviceTypesFilters$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), healthApiService = inject(HealthApiService)) =>
    actions$.pipe(
      ofType(HealthMonitorActions.getFiltersCount, HealthMonitorActions.updateFiltersCount),
      withLatestFrom(store.select(selectDeviceTypesFilters), store.select(selectHealthMonitorFiltersPayload)),
      exhaustMap(([, deviceTypesFilters, filtersPayload]) => {
        const reportGroupPayload = filtersPayload.find(f => f.filterType === HealthFilterName.ReportGroup);
        const reportGroupUIDs = (reportGroupPayload?.filteredValues ?? []) as string[];

        return healthApiService.getDevicesCount(reportGroupUIDs).pipe(
          map((count: { [key: string]: number }) =>
            HealthMonitorActions.getFiltersCountSuccess({
              filters: deviceTypesFilters.map(chip => ({
                ...chip,
                count: count[getHealthFilterCountProperty(chip.type)]
              }))
            })
          ),
          catchError((error: { message: string }) =>
            of(HealthMonitorActions.getFiltersCountFailure({ error: error.message }))
          )
        );
      })
    ),
  { functional: true }
);

export const loadHealthStatuses$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), healthApiService = inject(HealthApiService)) =>
    actions$.pipe(
      ofType(HealthMonitorActions.getFiltersCountSuccess),
      withLatestFrom(store.select(selectOtherWidgetFilterPayload), store.select(selectHealthMonitorFiltersPayload)),
      exhaustMap(([, payload, reportGroupPayload]) =>
        healthApiService.getStatuses(payload.concat(reportGroupPayload)).pipe(
          map(devices =>
            HealthMonitorActions.getHealthStatusesSuccess({
              healthStatuses: createHealthStatistic(devices)
            })
          ),
          catchError((error: { message: string }) =>
            of(HealthMonitorActions.getHealthStatusesFailure({ error: error.message }))
          )
        )
      )
    ),
  { functional: true }
);

export const loadReadRateStatistic$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), healthApiService = inject(HealthApiService)) =>
    actions$.pipe(
      ofType(HealthMonitorActions.getFiltersCountSuccess),
      withLatestFrom(
        store.select(selectHealthReadRateWidgetFilterPayload),
        store.select(selectHealthMonitorFiltersPayload),
        store.select(selectSelectedDevicesFilters)
      ),
      exhaustMap(([, payload, reportGroupPayload, selectedFilter]) => {
        if (selectedFilter.length === 1 && selectedFilter[0] === 'dcu') {
          return of(
            HealthMonitorActions.getReadRateStatisticSuccess({
              readRateStatistic: initialReadRateStatistic
            })
          );
        }

        return healthApiService.getReadRate(payload.concat(reportGroupPayload)).pipe(
          map(devices =>
            HealthMonitorActions.getReadRateStatisticSuccess({
              readRateStatistic: calculateReadRateStatistic(devices)
            })
          ),
          catchError((error: { message: string }) =>
            of(HealthMonitorActions.getReadRateStatisticFailure({ error: error.message }))
          )
        );
      })
    ),
  { functional: true }
);

export const loadReadSuccessRateStatistic$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), healthApiService = inject(HealthApiService)) =>
    actions$.pipe(
      ofType(HealthMonitorActions.getFiltersCountSuccess),
      withLatestFrom(
        store.select(selectReadSuccessRateWidgetFilterPayload),
        store.select(selectHealthMonitorFiltersPayload),
        store.select(selectSelectedDevicesFilters)
      ),
      exhaustMap(([, , reportGroupPayload, selectedFilters]) => {
        const isMtuSelected = selectedFilters.length === 0 || !!selectedFilters.find(ft => ft === 'mtu');
        if (!isMtuSelected) {
          return of(
            HealthMonitorActions.getReadSuccessRateStatisticSuccess({
              readSuccessRateStatistic: { readSuccessRate24Hours: null, readSuccessRate72Hours: null }
            })
          );
        }

        const reportGroupFromPayload = reportGroupPayload.find(f => f.filterType === HealthFilterName.ReportGroup);
        const reportGroupUIDs = (reportGroupFromPayload?.filteredValues ?? []) as string[];

        return healthApiService.getReadSuccessRate({ reportGroups: reportGroupUIDs }).pipe(
          map(statistics =>
            HealthMonitorActions.getReadSuccessRateStatisticSuccess({ readSuccessRateStatistic: statistics })
          ),
          catchError((error: { message: string }) =>
            of(HealthMonitorActions.getReadSuccessRateStatisticFailure({ error: error.message }))
          )
        );
      })
    ),
  { functional: true }
);

export const loadHealthBatteryStatistic$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), healthApiService = inject(HealthApiService)) =>
    actions$.pipe(
      ofType(HealthMonitorActions.getFiltersCountSuccess),
      withLatestFrom(
        store.select(selectBatteryCapacityWidgetFilterPayload),
        store.select(selectHealthMonitorFiltersPayload)
      ),
      exhaustMap(([, payload, reportGroupPayload]) =>
        healthApiService.getBatteryCapacity(payload.concat(reportGroupPayload)).pipe(
          map(devices =>
            HealthMonitorActions.getHealthBatteryStatisticSuccess({
              batteryStatistic: calculateTotalCountBatteryCapacity(devices),
              batteryDevicesIsNotEmpty: Boolean(devices.length)
            })
          ),
          catchError((error: { message: string }) =>
            of(HealthMonitorActions.getHealthBatteryStatisticFailure({ error: error.message }))
          )
        )
      )
    ),
  { functional: true }
);

export const loadHistoryLog$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    healthApiService = inject(HealthApiService),
    timezoneService = inject(TimezoneService)
  ) =>
    actions$.pipe(
      ofType(HealthMonitorActions.getHistoryLog),
      filter(key => 'date' in key),
      withLatestFrom(store.select(selectOtherWidgetFilterPayload), store.select(selectHealthMonitorFiltersPayload)),
      exhaustMap(([{ date }, payload, reportGroupPayload]) => {
        const { start, end } = timezoneService.getDatepickerRange(date!);
        return healthApiService
          .getHistoryLog([
            ...payload,
            ...reportGroupPayload,
            {
              filterType: HealthFilterName.DateTimeUtcSetPeriodStartDate,
              filteredValues: [DateUtil.toISOString(start)]
            },
            {
              filterType: HealthFilterName.DateTimeUtcSetPeriodEndDate,
              filteredValues: [DateUtil.addOneDayToString(end)]
            }
          ])
          .pipe(
            map(statistics =>
              HealthMonitorActions.getHistoryLogSuccess({
                historyLog: statistics.map((stat: HistoryUpdate) => ({
                  content: stat.updateTypeName,
                  amount: stat.totalCount,
                  updateType: stat.updateType!
                }))
              })
            ),
            catchError((error: { message: string }) =>
              of(HealthMonitorActions.getHistoryLogFailure({ error: error.message }))
            )
          );
      })
    ),
  { functional: true }
);

export const loadAlarmStatistics$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    healthApiService = inject(HealthApiService),
    timezoneService = inject(TimezoneService)
  ) =>
    actions$.pipe(
      ofType(HealthMonitorActions.getAlarmStatistics),
      filter(key => 'date' in key),
      withLatestFrom(
        store.select(selectAlarmsStatisticsWidgetFilterPayload),
        store.select(selectHealthMonitorFiltersPayload)
      ),
      exhaustMap(([{ date }, payload, reportGroupPayload]) => {
        const { start, end } = timezoneService.getDatepickerRange(date!);
        return healthApiService
          .getStatistics([
            ...payload,
            ...reportGroupPayload,
            {
              filterType: HealthFilterName.DateTimeUtcUpdatedPeriodStartDate,
              filteredValues: [DateUtil.toISOString(start)]
            },
            {
              filterType: HealthFilterName.DateTimeUtcUpdatedPeriodEndDate,
              filteredValues: [DateUtil.addOneDayToString(end)]
            }
          ])
          .pipe(
            map(alarmStatistics => HealthMonitorActions.getAlarmStatisticsSuccess({ alarmStatistics })),
            catchError((error: { message: string }) =>
              of(HealthMonitorActions.getAlarmStatisticsFailure({ error: error.message }))
            )
          );
      })
    ),
  { functional: true }
);

export const saveFiltersState$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), userConfigService = inject(UserConfigService)) =>
    actions$.pipe(
      ofType(HealthMonitorActions.savePersistedFiltersState),
      withLatestFrom(
        store.select(selectHealthMonitorFiltersPayload),
        store.select(selectSelectedDevicesFilters),
        store.select(selectAlarmsDateRange),
        store.select(selectHistoryLogDateRange)
      ),
      tap(([, payload, selectedDeviceTypes, alarmsDateRangeZoned, historyLogDateRangeZoned]) => {
        return userConfigService.save(USER_CONFIG_FILTERS_STORAGE_KEY, {
          reportGroupIds: payload.find(f => f.filterType === HealthFilterName.ReportGroup)?.filteredValues ?? [],
          selectedDeviceTypes,
          alarmsDateRange: alarmsDateRangeZoned,
          historyLogDateRange: historyLogDateRangeZoned
        } as UserConfigHealthMonitorFilters);
      })
    ),
  { dispatch: false, functional: true }
);
